import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import type { PipelineMetricsResponse } from '../../types/ai-pipeline';

export interface MetricsPanelProps {
  readonly metrics: PipelineMetricsResponse | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
}

interface MetricCardProps {
  readonly label: string;
  readonly value: number | undefined;
  readonly isLoading: boolean;
}

function MetricCard({ label, value, isLoading }: MetricCardProps): React.ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-8 w-24 animate-pulse rounded bg-muted" aria-hidden="true" />
        ) : (
          <p className="text-3xl font-bold">{value?.toLocaleString() ?? '—'}</p>
        )}
        <p className="mt-1 text-sm text-muted-foreground">Last 24 hours</p>
      </CardContent>
    </Card>
  );
}

/**
 * Displays pipeline throughput metrics (Knowledge Units, Vectors, Relationship Edges)
 * as a responsive grid of metric cards with loading and error states.
 */
export function MetricsPanel({ metrics, isLoading, error, onRetry }: MetricsPanelProps): React.ReactElement {
  if (error) {
    return (
      <Alert variant="destructive" aria-live="assertive">
        <AlertTitle>Failed to load metrics</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>{error}</span>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-4"
      aria-live="polite"
      aria-label="Pipeline metrics"
    >
      <MetricCard
        label="Knowledge Units"
        value={metrics?.knowledgeUnitsProcessed}
        isLoading={isLoading}
      />
      <MetricCard
        label="Vectors Generated"
        value={metrics?.vectorsGenerated}
        isLoading={isLoading}
      />
      <MetricCard
        label="Relationship Edges"
        value={metrics?.relationshipEdgesComputed}
        isLoading={isLoading}
      />
    </div>
  );
}
